<template>
  <Layout>
    <!-- Header -->
    <div class="row">
      <div class="col-6">
        <div class="page-title-box d-flex align-items-center justify-content-between">
          <h4 class="mb-0 font-size-18">Incident Management</h4>
        </div>
      </div>
      <div class="col-6 text-end">
        <button class="btn btn-sm btn-outline-warning m-2" @click="bulkResolveAllIncidents()">
          <i class="mdi mdi-check"></i> Resolve All
        </button>
        <button class="btn btn-sm btn-outline-success m-2" v-if="bulkResolveList.length > 0"
          @click="closeSelectedIncidents()">
          <i class="mdi mdi-check"></i> Resolve Selected Incidents
        </button>
      </div>
    </div>
    <!-- Statistics -->
    <div class="row">
      <div class="col-sm-3">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="me-3">
                <p class="text-muted mb-2">Total Incidents</p>
                <h5 class="mb-0" v-if="incidentStatistics">
                  {{ incidentStatistics.totalIncidents }}
                </h5>
              </div>

              <div class="avatar-sm ms-auto">
                <div class="avatar-title bg-light rounded-circle text-danger font-size-20">
                  <i class="bx bxs-car"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-3">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="me-3">
                <p class="text-muted mb-2">Open Incidents</p>
                <h5 class="mb-0" v-if="incidentStatistics">
                  {{ incidentStatistics.openIncidents }}
                </h5>
              </div>

              <div class="avatar-sm ms-auto">
                <div class="avatar-title bg-light rounded-circle text-warning font-size-20">
                  <i class="bx bxs-folder-open"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-3">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="me-3">
                <p class="text-muted mb-2">Resolved Incidents</p>
                <h5 class="mb-0" v-if="incidentStatistics">
                  {{ incidentStatistics.resolvedIncidents }}
                </h5>
              </div>

              <div class="avatar-sm ms-auto">
                <div class="avatar-title bg-light rounded-circle text-success font-size-20">
                  <i class="bx bxs-check-circle"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-sm-3">
        <div class="card mini-stats-wid">
          <div class="card-body">
            <div class="d-flex flex-wrap">
              <div class="me-3">
                <p class="text-muted mb-2">Challans Generated</p>
                <h5 class="mb-0" v-if="incidentStatistics">
                  {{ incidentStatistics.challanGenerated }}
                </h5>
              </div>

              <div class="avatar-sm ms-auto">
                <div class="avatar-title bg-light rounded-circle text-primary font-size-20">
                  <i class="bx bxs-file"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Incidents Table -->
    <div class="table-responsive mb-0">
      <b-table class="datatables" :items="allIncidents" :fields="fields" responsive="sm" :per-page="0"
        :current-page="currentPage" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :filter="filter"
        :filter-included-fields="filterOn" @filtered="onFiltered">
        <template #cell(check)="row">
          <input :disabled="row.item.status !== 'Open'" class="form-check-input" type="checkbox"
            @change="updateBulkResolveList($event, row.item.id)" />
        </template>
        <template #cell(vehicle_no)="row">
          {{ !row.item.vehicle_no ? "NA" : row.item.vehicle_no }}
        </template>
        <template #cell(sub_category_name)="row">
          {{ !row.item.sub_category_name ? "NA" : row.item.sub_category_name }}
        </template>
        <template #cell(reported_to_name)="row">
          {{
            !row.item.reported_to_name ? "System" : row.item.reported_to_name
          }}
        </template>
        <template #cell(status)="row">
          <span :class="`badge ${getStatusClass(row.item.status)}`">{{
            row.item.status
          }}</span>
        </template>

        <template #cell(challan_status)="row">
          <span :class="`badge ${getChallanStatusClass(row.item.challan_status)}`"
            v-if="row.item.category_name === 'Incident'">{{ row.item.challan_status }}</span>

          <span v-else>NA</span>
        </template>

        <template #cell(actions)="row">
          <b-button size="sm" variant="primary" class="m-1" @click="showIncidentDetails(row.item.id)">
            View Details
          </b-button>
          <b-button size="sm" variant="info" class="m-1" v-if="row.item.status === 'Open'"
            @click="showTrackIncidentDetails(row.item)">
            Track RPO
          </b-button>
          <b-button v-if="row.item.status === 'Open'" size="sm" variant="success" class="m-1"
            @click="openResolveIncidentPage(row.item.id)">
            Resolve
          </b-button>
          <!-- <b-button
            variant="warning"
            class="m-1"
            size="sm"
            @click="showChallanDetails(row.item)"
            v-show="row.item.category_name !== 'Accident'"
            :disabled="row.item.challan_status !== 'Not Generated'"
          >
            Raise Challan
          </b-button> -->
          <b-button @click="showTrackIncidentDetails(row.item)" variant="info" class="m-1" size="sm"
            :disabled="row.item.status !== 'Open'" v-show="row.item.category_name === 'Accident'">
            Track Incident
          </b-button>
        </template>
      </b-table>
    </div>
    <div class="row">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-end mb-3">
          <ul class="pagination pagination-rounded mb-0">
            <!-- pagination -->
            <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
          </ul>
        </div>
      </div>
    </div>

    <!-- Incident details modal -->
    <b-modal id="more-details-modal" size="xl" title-class="font-18" hide-footer centered>
      <template #modal-header>
        <div>
          <h5>Incident Details</h5>
        </div>
        <div>
          <button type="button" @click="updateIncidentDetails()" class="btn btn-sm btn-primary mb-2 m-2">
            <i class="mdi mdi-content-save-all me-1"></i> Save Incident
          </button>
          <button class="btn btn-sm btn-outline-primary m-2" disabled>
            Status: {{ selectedIncident.status }}
          </button>
          <button class="btn btn-sm btn-outline-info m-2" @click="sendNotificationToRPO(selectedIncident.id)" v-show="selectedIncident.status === 'Open' &&
            selectedIncident.category_name == 'Accident'
            ">
            <i class="bx bx-notification"></i> Send Notification to RPO
          </button>
          <button class="btn btn-sm btn-outline-info m-2" @click="openChallanModal()" v-show="selectedIncident.status === 'Open' &&
            selectedIncident.category_name == 'Incident' &&
            selectedIncident.challan_status == 'Not Generated'
            ">
            <i class="bx bx-notification"></i> Generate Challan
          </button>
          <button class="btn btn-sm btn-outline-success m-2" @click="showResolveConfirmation()"
            v-show="selectedIncident.status === 'Open'">
            <i class="mdi mdi-check"></i> Mark as Resolved
          </button>
          <button class="btn btn-sm btn-outline-danger m-2" @click="$bvModal.hide('more-details-modal')">
            <i class="mdi mdi-close"></i> Close
          </button>
        </div>
      </template>

      <div class="row">
        <div class="col-xl-6">
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table mb-0">
                  <tbody>
                    <tr>
                      <th scope="row">Incident Id</th>
                      <td>{{ selectedIncident.id }}</td>
                    </tr>
                    <tr>
                      <th scope="row">Vehicle Number</th>
                      <td contenteditable @input="onInput">
                        {{
                          selectedIncident.vehicle_no != ""
                          ? selectedIncident.vehicle_no
                          : "NA"
                        }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Vehicle Class</th>
                      <td>
                        <v-select v-model="selectedIncident.class_name" :options="options.vehicleClass" label="class_name"
                          :reduce="(vc) => vc.class_name"></v-select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Incident Category</th>
                      <td>
                        {{
                          selectedIncident.category_name != ""
                          ? selectedIncident.category_name
                          : "NA"
                        }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Sub Category</th>
                      <td>{{ selectedIncident.sub_category_name }}</td>
                    </tr>
                    <tr>
                      <th scope="row">Source</th>
                      <td>{{ selectedIncident.source_name }}</td>
                    </tr>
                    <tr>
                      <th scope="row">Description</th>
                      <td>{{ selectedIncident.description }}</td>
                    </tr>
                    <tr v-show="selectedIncident.category_name === 'Accident'">
                      <th scope="row">Chainage Number</th>
                      <td>{{ selectedIncident.ch_no }}</td>
                    </tr>
                    <tr v-show="selectedIncident.category_name === 'Accident'">
                      <th scope="row">Side</th>
                      <td>{{ selectedIncident.side_name }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl-6">
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table mb-0">
                  <tbody>
                    <tr>
                      <th scope="row">Assigned To</th>
                      <td>
                        <v-select v-model="selectedIncident.reported_to_name" :options="options.informedTo"
                          label="firstName" :reduce="(vc) => vc.firstName"></v-select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Challan Status</th>
                      <td>
                        {{ selectedIncident.challan_status }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Incident Date/Time</th>
                      <td>
                        {{
                          getReadableDateTime(
                            selectedIncident.incident_datetime
                          )
                        }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Reported Date/Time</th>
                      <td>
                        {{
                          new Date(
                            selectedIncident.reported_datetime
                          ).toLocaleString()
                        }}
                      </td>
                    </tr>
                    <tr v-show="selectedIncident.category_name === 'Accident'">
                      <th scope="row">Informed To</th>
                      <td>{{ selectedIncident.reported_to_name }}</td>
                    </tr>

                    <tr v-show="selectedIncident.category_name === 'Accident'">
                      <th scope="row">Driver Name</th>
                      <td>{{ selectedIncident.driver_name }}</td>
                    </tr>
                    <tr v-show="selectedIncident.category_name === 'Accident'">
                      <th scope="row">No. of Injured People</th>
                      <td>{{ selectedIncident.injured_count }}</td>
                    </tr>
                    <tr v-show="selectedIncident.category_name === 'Accident'">
                      <th scope="row">Response Date/Time</th>
                      <td>{{ selectedIncident.response_datetime }}</td>
                    </tr>
                    <tr v-show="selectedIncident.category_name === 'Accident'">
                      <th scope="row">RPO Comment</th>
                      <td>{{ selectedIncident.comment }}</td>
                    </tr>
                    <tr v-show="selectedIncident.status !== 'Open'">
                      <th scope="row">Close Date/Time</th>
                      <td>{{ selectedIncident.close_datetime }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Incident Photos</h4>
              <img v-for="(src, index) in incidentImages" :key="index" class="float-left image"
                @click="() => showCaptiomImg(index)" width="150" :src="src" />
            </div>
            <vue-easy-lightbox :visible="visibleCaption" :imgs="incidentImages" :index="index"
              @hide="handleCaptionHide"></vue-easy-lightbox>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Raise challan modal -->
    <b-modal id="raise-challan-modal" title="Raise Challan" title-class="font-18" size="xl" hide-footer centered>
      <template #modal-header>
        <div>
          <h5>Challan Details</h5>
        </div>
        <div>
          <button class="btn btn-sm btn-outline-success m-2" @click="generateChallan()"
            v-show="selectedIncident.challan_status == 'Not Generated'">
            <i class="mdi mdi-check"></i> Generate Challan
          </button>
          <button class="btn btn-sm btn-outline-danger" @click="$bvModal.hide('raise-challan-modal')">
            <i class="mdi mdi-close"></i> Close
          </button>
        </div>
      </template>

      <div class="row">
        <div class="col-6">
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table mb-0">
                  <tbody>
                    <tr>
                      <th scope="row">Offence Id</th>
                      <td>
                        <v-select v-model="offenceId" :options="options.offenceId" label="offenceData"
                          :reduce="(vc) => vc.offence_id"></v-select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Department</th>
                      <td>
                        <v-select v-model="dpCd" :options="options.dpCd" label="name"
                          :reduce="(vc) => vc.name"></v-select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Violation Source</th>
                      <td>
                        <v-select v-model="voilationSource" :options="options.violationSrc" label="name"
                          :reduce="(vc) => vc.name"></v-select>
                      </td>
                    </tr>
                    <tr>
                      <th>State Code</th>
                      <td>
                        <v-select v-model="stateCd" :options="options.stateCode" label="stateData"
                          :reduce="(vc) => vc.state_code"></v-select>
                      </td>
                    </tr>
                    <tr>
                      <th>Vehicle Speed</th>
                      <td>
                        <input type="number" placeholder="Please enter vehicle speed"
                          class="form-control bg-dark text-light" aria-label="Default"
                          aria-describedby="inputGroup-sizing-default" v-model="vehicleSpeed" />
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Vehicle Number</th>
                      <td>
                        {{ selectedIncident.vehicle_no }}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">User Id</th>
                      <td>2</td>
                    </tr>

                    <tr>
                      <th scope="row">Violation Source category</th>
                      <td>
                        {{ selectedIncident.sub_category_name }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="col-6">
          <div class="card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table mb-0">
                  <tbody>
                    <tr>
                      <th>Location</th>
                      <td>{{ selectedIncident.location }}</td>
                    </tr>
                    <tr>
                      <th>Code Of RTO</th>
                      <td>offcd</td>
                    </tr>
                    <tr>
                      <th>Vendor Name</th>
                      <td>VEH</td>
                    </tr>
                    <tr>
                      <th>Violation Time</th>
                      <td>{{ selectedIncident.incident_datetime }}</td>
                    </tr>
                    <tr>
                      <th>Latitude</th>
                      <td>{{ selectedIncident.lat }}</td>
                    </tr>
                    <tr>
                      <th>Longitude</th>
                      <td>{{ selectedIncident.long }}</td>
                    </tr>

                    <tr>
                      <th>Speed Limit</th>
                      <td>{{ selectedIncident.speed_limit }}</td>
                    </tr>

                    <tr>
                      <th>Equipment Id</th>
                      <td>{{ selectedIncident.camera_id }}</td>
                    </tr>
                    <tr>
                      <th scope="row">Transaction Number</th>
                      <td>
                        {{ selectedIncident.transaction_id }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Incident Photos</h4>
              <hr />
              <img v-for="(src, index) in incidentImages" :key="index" class="float-left image"
                @click="() => showCaptiomImg(index)" :src="src" />
            </div>
            <vue-easy-lightbox :visible="visibleCaption" :imgs="incidentImages" :index="index"
              @hide="handleCaptionHide"></vue-easy-lightbox>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Track Incident modal -->
    <b-modal id="track-incident-modal" title="Track Incident" title-class="font-18" size="xl" hide-footer centered
      no-close-on-esc no-close-on-backdrop>
      <template #modal-header>
        <div>
          <h5>RPO and Incident Live Tracker</h5>
        </div>
        <div>
          <router-link :to="`/incident/view-accident-details/${selectedIncident.id}`">
            <button class="btn btn-sm btn-outline-info m-2">
              Show Accident Details
            </button>
          </router-link>
          <button class="btn btn-sm btn-outline-danger" @click="closeTrackRPOModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
      </template>
      <div class="row">
        <div class="col-5">
          <p class="mb-2">
            Incident ID:
            <span class="text-primary">{{ selectedIncident.id }}</span>
          </p>
          <p class="mb-2">
            Vehicle Number:
            <span class="text-primary">{{
              selectedIncident.vehicle_no
              ? selectedIncident.vehicle_no
              : "Not available"
            }}</span>
          </p>
          <p class="mb-4">
            Date | Time:
            <span class="text-primary">{{
              new Date(selectedIncident.incident_datetime).toLocaleString()
            }}</span>
          </p>
          <hr />
          <h5>
            <i class="bx bxs-video-recording text-danger font-size-18 bx-fade-right"></i>
            <span class="m-3">Live</span>
          </h5>
          <div class="row" v-if="rpoDeviceStats">
            <div class="col-6">
              <button v-if="rpoGPS" :class="rpoGPS.status
                ? 'btn btn-sm w-100 btn-success m-2'
                : 'btn btn-sm w-100 btn-danger m-2'
                ">
                RPO GPS Location
              </button>
            </div>
            <div class="col-6">
              <button v-if="rpoFrontCamera" :class="rpoFrontCamera.status
                ? 'btn btn-sm w-100 btn-success m-2'
                : 'btn btn-sm w-100 btn-danger m-2'
                ">
                RPV Front Camera
              </button>
            </div>
            <div class="col-6">
              <button v-if="rpoRearCamera" :class="rpoRearCamera.status
                ? 'btn btn-sm w-100 btn-success m-2'
                : 'btn btn-sm w-100 btn-danger m-2'
                ">
                RPV Rear Camera
              </button>
            </div>
            <div class="col-6">
              <button v-if="rpoBodyCamera" :class="rpoBodyCamera.status
                ? 'btn btn-sm w-100 btn-success m-2'
                : 'btn btn-sm w-100 btn-danger m-2'
                ">
                RPO Body Camera
              </button>
            </div>
          </div>
          <div class="text-center" v-else>
            <p class="text-danger">RPO Device is Offline</p>
          </div>
          <hr />
          <ul class="verti-timeline list-unstyled">
            <li class="event-list active">
              <div class="event-timeline-dot">
                <i class="bx bxs-right-arrow-circle font-size-18 bx-fade-right"></i>
              </div>
              <div class="d-flex">
                <div class="me-3">
                  <h5 class="font-size-14">
                    Incident Closed
                    <i class="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
                  </h5>
                </div>
                <div class="flex-grow-1">
                  <div>14/03/2023 - 18:05:19</div>
                </div>
              </div>
            </li>
            <li class="event-list">
              <div class="event-timeline-dot">
                <i class="bx bx-right-arrow-circle font-size-18"></i>
              </div>
              <div class="d-flex">
                <div class="me-3">
                  <h5 class="font-size-14">
                    RPO Reported
                    <i class="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
                  </h5>
                </div>
                <div class="flex-grow-1">
                  <div>14/03/2023 - 17:56:10</div>
                </div>
              </div>
            </li>
            <li class="event-list">
              <div class="event-timeline-dot">
                <i class="bx bx-right-arrow-circle font-size-18"></i>
              </div>
              <div class="d-flex">
                <div class="me-3">
                  <h5 class="font-size-14">
                    Incident Created
                    <i class="bx bx-right-arrow-alt font-size-16 text-primary align-middle ms-2"></i>
                  </h5>
                </div>
                <div class="flex-grow-1">
                  <div>14/03/2023 - 17:40:37</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div class="col-7">
          <h5>RPO Activity Tracking</h5>
          <GoogleMap v-if="rpoDeviceStats" :location="rpoGPS" />
          <div class="text-center" v-else>
            <p class="text-danger mt-5">
              Unable to load location. RPO Device is Offline.
            </p>
          </div>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from "@/layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";
import RaiseNewIncident from "./raise-incident.vue";
import VueEasyLightbox from "vue-easy-lightbox";
import Swal from "sweetalert2";
import rpoMap from "../../views/incident/rpoVehicleMap.vue";
import incidentMixin from "../../mixins/incident";
import { mapActions } from "vuex";
import GoogleMap from "@/components/incidents/google-map";

/**
 * Dashboard Component
 */
export default {
  page: {
    title: "Incident Management",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },

  mixins: [incidentMixin],

  components: {
    Layout,
    PageHeader,
    RaiseNewIncident,
    VueEasyLightbox,
    rpoMap,
  },

  data () {
    return {
      filterVehicleNumber: null,
      filterVehicleSpeed: null,
      subCategoryId: null,
      vehicleNumber: null,
      vehicleClass: null,
      allIncidents: [],
      incidentId: null,
      visibleCaption: false,
      selectedIncident: {},
      resolveComment: "",
      index: 1,
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      sortBy: "id",
      sortDesc: true,
      filterOn: [],
      filter: null,
      offenceId: null,
      dpCd: null,
      latitude: null,
      voilationSource: null,
      stateCd: null,
      districtId: null,
      offCd: null,
      equipmentId: null,
      vendorName: null,
      vehicleSpeed: 0,
      speedLimit: null,
      vehicleWeight: null,
      voilationTime: null,
      actionTime: null,
      longitude: null,
      rpoDeviceStats: null,
      bulkResolveList: [],
      fields: [
        {
          key: "check",
          label: "",
          sortable: false,
        },
        {
          key: "id",
          label: "ID",
          sortable: true,
        },
        {
          key: "vehicle_no",
          label: "Vehicle No.",
          sortable: false,
        },
        {
          key: "ch_no",
          label: "Location",
          sortable: false,
        },
        {
          key: "sub_category_name",
          label: "Category",
          sortable: false,
        },
        {
          key: "reported_to_name",
          label: "Assigned RPO",
          sortable: false,
        },
        {
          key: "reported_datetime",
          label: "Date | Time",
          sortable: true,
          formatter: (value) => {
            return this.getReadableDateTime(value);
          },
        },
        {
          key: "status",
          sortable: false,
        },
        {
          key: "actions",
          sortable: false,
          label: "Action",
        },
      ],

      options: {
        vehicleClass: [],
        offenceId: [],
        districtCode: [],
        stateCode: [],
        dpCd: [{ name: "TD" }, { name: "TR" }],
        violationSrc: [{ name: "SLV" }, { name: "RLV" }],
        subCategory: [],
        informedTo: [],
      },
      socketInterval: null,
    };
  },

  methods: {
    ...mapActions("incident", ["updateIncidents"]),

    async bulkResolveAllIncidents () {
      Swal.fire({
        title: "Are you sure?",
        text: "You cannot revert once all incidents are resolved",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        denyButtonText: "Cancel"
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.ResolveBulkIncidents()
          Swal.fire("Done!", "All incidents have been resolved", "success");
        }
      });
    },
    async closeSelectedIncidents () {
      Swal.fire({
        title: "Are you sure?",
        text: "You cannot revert once all selected incidents are resolved",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        denyButtonText: "Cancel"
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.ResolveSelectedIncidents({
            incidents: this.bulkResolveList
          })

          this.bulkResolveList = []
          Swal.fire("Done!", "Selected incidents have been resolved", "success");
        }
      });
    },
    updateBulkResolveList (event, id) {
      if (event.target.checked) {
        this.bulkResolveList.push(id)
      } else {
        this.bulkResolveList.splice(this.bulkResolveList.indexOf(id), 1)
      }
    },
    getStatusClass (status) {
      switch (status) {
        case "Open":
          return "bg-danger";

        case "In Progress":
          return "bg-warning";

        case "Resolved":
          return "bg-success";

        case "Closed":
          return "bg-success";

        default:
          return "bg-info";
      }
    },

    getChallanStatusClass (status) {
      switch (status) {
        case "Generated":
          return "bg-primary";

        case "Not Generated":
          return "bg-danger";

        case "Paid":
          return "bg-success";

        case "Unpaid":
          return "bg-warning";

        case "Approved":
          return "bg-info";

        default:
          return "bg-info";
      }
    },

    gotoRaiseNewIncidentPage () {
      this.$router.push("/incident/raise-incident");
    },

    sendNotificationToRPO () {
      this.$bvModal.show("select-rpo-modal");
    },

    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    getReadableDateTime (dateTime) {
      if (dateTime) {
        // const date = String(dateTime).split("T");
        return `${new Date(dateTime).toLocaleString()}`;
      }
    },

    showCaptiomImg (index) {
      this.index = index;
      this.visibleCaption = true;
    },

    handleCaptionHide () {
      this.visibleCaption = false;
    },

    async generateChallan () {
      try {
        let images = [];
        for (let image of this.selectedIncident.images) {
          images.push(image.media_url);
        }

        const body = {
          incidentId: this.selectedIncident.id,
          id: "2",
          offenceId: this.offenceId,
          dpCd: this.dpCd,
          latitude: this.selectedIncident.lat,
          transactionNo: this.selectedIncident.transaction_id,
          userId: "1",
          regnNo: this.selectedIncident.vehicle_no,
          voilationSource: this.voilationSource,
          voilationSourceCatg: this.selectedIncident.sub_category_name,
          stateCd: this.stateCd,
          districtId: this.districtId,
          location: this.selectedIncident.location,
          offCd: 55,
          equipmentId: this.selectedIncident.camera_id,
          vendorName: "VEH",
          vehicleSpeed: parseInt(this.vehicleSpeed),
          speedLimit: this.selectedIncident.speed_limit,
          vehicleWeight: 0,
          voilationTime: this.selectedIncident.incident_datetime,
          actionTime: new Date(),
          longitude: this.selectedIncident.long,
          images: images,
        };

        await this.GenerateChallan(body);
        await this.updateIncidentDetails();
        await this.challanGenerated();
        this.$bvModal.hide("more-details-modal");
      } catch (e) {
        console.error(e);
        Swal.fire(
          "Challan Generation Failed",
          e.response.data.message,
          "error"
        );
      }
    },

    async openChallanModal () {
      this.$bvModal.show("raise-challan-modal");
    },

    async onInput (e) {
      this.vehicleNumber = await e.target.innerText;
    },

    async getAllIncidents () {
      const result = await this.GetAllIncidents(this.currentPage);
      this.perPage = result.rows;
      this.totalRows = result.totalPageNumber;
      this.allIncidents = result.data;
    },

    async getIncidentMasters () {
      try {
        const incidents = await this.GetIncidentMasters();
        this.options.vehicleClass = incidents.getVehicleClass;
        this.options.offenceId = incidents.getOffenceId;
        this.options.districtCode = incidents.getDistrictCode;
        this.options.stateCode = incidents.getStateCode;
        this.options.subCategory = incidents.getIncidentSubCategory;
        this.options.informedTo = incidents.rpoData;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        Swal.fire(
          "Network Error",
          "Failed to fetch data from the server. Please check your internet connection.",
          "error"
        );
      }
    },

    getIncidentById () {
      this.$socket.emit(
        "REQUEST:GET_INCIDENT_BY_ID",
        JSON.stringify({
          incidentId: this.incidentId,
          filterVehicleNumber: this.filterVehicleNumber,
          filterVehicleSpeed: this.filterVehicleSpeed,
        })
      );
    },

    resetFilter () {
      this.incidentId = "";
      this.filterVehicleNumber = "";
      this.filterVehicleSpeed = "";
    },

    getOffence (offence) {
      return `${offence.offence_id} - ${offence.offence}`;
    },

    informedToRpo () {
      Swal.fire(
        "RPO Selected!",
        "RPO has been selected and notifications have been sent.",
        "success"
      ).then(() => {
        this.$bvModal.hide("select-rpo-modal");
      });
    },

    showIncidentDetails (id) {
      this.$router.push(`/incident/view-incident-details/${id}`);
    },

    openResolveIncidentPage (id) {
      this.$router.push(`/incident/resolve-incidents/${id}`);
    },

    showTrackIncidentDetails (incident) {
      this.selectedIncident = incident;
      this.$bvModal.show("track-incident-modal");
      this.trackingInterval = setInterval(() => {
        this.$socket.emit(
          "REQUEST:RPO_LIVE_DETAILS",
          this.selectedIncident.reported_to
        );
      }, 3000);

      this.$socket.emit(
        "REQUEST:RPO_LIVE_DETAILS",
        this.selectedIncident.reported_to
      );
    },

    closeTrackRPOModal () {
      this.$bvModal.hide("track-incident-modal");
      if (this.trackingInterval) clearInterval(this.trackingInterval);
      this.trackingInterval = null;
      this.rpoDeviceStats = null;
    },
  },

  computed: {
    incidentStatistics () {
      return this.$store.getters["socket/incident"].statistics;
    },
    incidents () {
      return this.$store.getters["socket/incident"].incidents;
    },
    incidentImages () {
      return this.selectedIncident.images
        ? this.selectedIncident.images.map((e) => e.media_url)
        : [];
    },
    rpoFrontCamera () {
      return !this.rpoDeviceStats
        ? false
        : this.rpoDeviceStats.find((a) => a.name === "front_camera_url");
    },
    rpoRearCamera () {
      return !this.rpoDeviceStats
        ? false
        : this.rpoDeviceStats.find((a) => a.name === "rear_camera_url");
    },
    rpoBodyCamera () {
      return !this.rpoDeviceStats
        ? false
        : this.rpoDeviceStats.find((a) => a.name === "body_camera_url");
    },
    rpoGPS () {
      return !this.rpoDeviceStats
        ? false
        : this.rpoDeviceStats.find((a) => a.name === "GPS");
    },
  },

  async mounted () {
    await this.getIncidentMasters();

    this.sockets.subscribe("GET_INCIDENT_BY_ID", (data) => {
      this.allIncidents = data;
    });

    this.options.offenceId.map(function (x) {
      return (x.offenceData = x.offence_id + " - " + x.offence);
    });

    this.options.stateCode.map(function (x) {
      return (x.stateData = x.state_code + " - " + x.name);
    });

    this.options.districtCode.map(function (x) {
      return (x.districtData = x.district_id + " - " + x.name);
    });
  },

  watch: {
    currentPage: async function () {
      await this.getAllIncidents();
    },
    incidents: async function () {
      this.allIncidents = this.incidents.data;
      this.perPage = this.incidents.rows;
      this.totalRows = this.incidents.totalPageNumber;
    },
  },

  created () {
    this.socketInterval = setInterval(() => {
      if (
        !this.incidentId &&
        !this.filterVehicleNumber &&
        !this.filterVehicleSpeed
      ) {

        this.$socket.emit("REQUEST:INCIDENT_STATISTICS");

        if (this.currentPage == 1) {
          this.$socket.emit("REQUEST:INCIDENT_DATA");
        }
      }

    }, 5000);

    this.$socket.emit("REQUEST:INCIDENT_STATISTICS");
    this.$socket.emit("REQUEST:INCIDENT_DATA");
  },

  beforeDestroy () {
    if (this.socketInterval) {
      clearInterval(this.socketInterval);
      this.socketInterval = null;
    }
  },
};
</script>

<style scoped>
.image {
  height: 100px;
  cursor: pointer;
  margin: 5px;
  display: inline-block;
}
</style>
